import { modelLoadingAtom } from '@/atoms';
import { LoadingBadge } from '@/components/ui/LoadingBadge';
import { useAtomValue } from 'jotai';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Loading rows for fragment (IFC) model layers: the full badge while the
 * .frag downloads and while its geometry is first built (without it a big
 * model shows scattered tiles with gaps for seconds and reads as broken).
 *
 * Later detail bursts after a zoom or pan are routine, so they get one small,
 * nameless spinner for all models instead of a badge per layer — a full badge
 * there popped up on every navigation and was noise.
 *
 * Entries are driven by `IFC2` via `modelLoadingAtom`. Renders rows only —
 * `ViewerLoadingColumn` positions and stacks it with the other loaders.
 */
export const ModelLoadingOverlay = () => {
  const { t } = useTranslation('project');
  const loading = useAtomValue(modelLoadingAtom);
  const entries = Object.values(loading);
  if (entries.length === 0) return null;

  const refining = entries.some((e) => e.phase === 'decoding' && e.stage === 'refine');
  const badges = entries.filter((e) => !(e.phase === 'decoding' && e.stage === 'refine'));

  return (
    <>
      {badges.map((e) => (
        <LoadingBadge
          key={e.layerId}
          name={e.layerName}
          phase={e.phase}
          progress={e.progress}
          loadedBytes={e.loadedBytes}
          totalBytes={e.totalBytes}
          error={e.error}
          labels={{
            fetching: t('modelLoader.fetching'),
            decoding: t('modelLoader.building'),
            failed: t('modelLoader.failed'),
          }}
        />
      ))}
      {refining && (
        <div
          role='status'
          data-testid='model-refining'
          className='self-center rounded-full bg-black/30 p-1 text-white/80 backdrop-blur-sm dark:bg-white/15'>
          <Loader2 className='size-3 animate-spin' aria-hidden />
          <span className='sr-only'>{t('modelLoader.refining')}</span>
        </div>
      )}
    </>
  );
};
